/**
 * 课程进度（本地存储）
 * ============================================================
 * Midoo 式课程需要"学到哪儿了"的概念，否则 15 课就是一盘散沙。
 *
 * 这里先用 localStorage 记录已完成课程，理由：
 *   1. 不依赖数据库（本地开发无 DATABASE_URL 时同样可用）；
 *   2. 不依赖任何 API，断网也能记录；
 *   3. 与项目既有的"本地优先"存储策略一致（deviceId、收藏、对话历史都在本地）。
 *
 * 后续接入真实账号体系时，这里可以平滑替换成服务端进度。
 */

const PROGRESS_KEY = 'learn_cantonese_course_progress';

/**
 * 读取进度数据
 * @returns {{ completed: string[], updatedAt: string|null, records: Object }}
 */
function readProgress() {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return { completed: [], updatedAt: null, records: {} };
    const parsed = JSON.parse(raw);
    return {
      completed: Array.isArray(parsed?.completed) ? parsed.completed : [],
      updatedAt: parsed?.updatedAt || null,
      /*
       * 每课完成时的「时间 + 词句数」，用于首页「最近 7 天」小结。
       *
       * 老数据只有 completed 数组、没有 records —— 不能因此崩，
       * 也不能把老课程当成"今天完成的"（那会让刚上线这功能时
       * 所有人的周报凭空多出十几课）。所以老课一律不计入周报，
       * 只有本功能上线后完成的课才有记录。
       */
      records: parsed?.records && typeof parsed.records === 'object' ? parsed.records : {},
    };
  } catch (e) {
    console.warn('[courseProgress] 读取进度失败:', e?.message);
    return { completed: [], updatedAt: null, records: {} };
  }
}

function writeProgress(data) {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('[courseProgress] 保存进度失败:', e?.message);
  }
}

/**
 * 取已完成课程 id 列表
 * @returns {string[]}
 */
export function getCompletedLessons() {
  return readProgress().completed;
}

/**
 * 判断某课是否已完成
 * @param {string} lessonId
 * @returns {boolean}
 */
export function isLessonCompleted(lessonId) {
  return readProgress().completed.includes(lessonId);
}

/**
 * 标记课程完成（幂等）
 *
 * @param {string} lessonId
 * @param {{vocab?:number, phrases?:number}} [meta] 这课的词汇/说法数量，
 *        用于首页「最近 7 天」小结。不传也能正常工作（记 0）。
 * @returns {string[]} 更新后的已完成列表
 */
export function markLessonCompleted(lessonId, meta = {}) {
  const data = readProgress();
  const now = new Date().toISOString();
  const rec = {
    at: now,
    vocab: Math.max(0, Number(meta.vocab) || 0),
    phrases: Math.max(0, Number(meta.phrases) || 0),
  };

  if (!data.completed.includes(lessonId)) {
    data.completed.push(lessonId);
    data.updatedAt = now;
    data.records[lessonId] = rec;
    writeProgress(data);
  } else if (!data.records[lessonId]) {
    /*
     * 已完成但没有记录 = 本功能上线前学的课。
     * 用 updatedAt 兜底：那是"最后一次学习时间"，比"现在"更接近事实；
     * 如果连 updatedAt 都没有，就干脆不记（宁可少算，也不要把旧课算成今天）。
     */
    if (data.updatedAt) {
      data.records[lessonId] = { ...rec, at: data.updatedAt };
      writeProgress(data);
    }
  }
  /*
   * ⚠️ 已完成的课**再次进入时不刷新时间**。
   * 否则学生反复重看同一课，周报会一天天长出"新课"，
   * 这种数字一旦虚高就再也没人信了。
   */
  return data.completed;
}

/**
 * 取消完成标记
 * @param {string} lessonId
 * @returns {string[]}
 */
export function unmarkLessonCompleted(lessonId) {
  const data = readProgress();
  data.completed = data.completed.filter((id) => id !== lessonId);
  data.updatedAt = new Date().toISOString();
  // 顺手删掉完成记录：否则「取消 → 重新学完」会沿用旧时间，
  // 这节课就永远不算进它实际完成的那一周。宁可丢一点历史，也不能算错周。
  delete data.records[lessonId];
  writeProgress(data);
  return data.completed;
}

/**
 * 最近 N 天的学习小结
 *
 * ── 为什么是「最近 7 天」而不是「本周（周一至周日）」──────────
 * 按自然周算，周一早上打开永远是 0 —— 用户刚学了东西却看到一片零，
 * 会觉得"没记上"。滚动 7 天任何一天打开都能看到真实进展，
 * 也更贴近"我这几天学得怎么样"这个直觉问题。
 *
 * @param {number} [days=7]
 * @returns {{days:number, lessons:number, vocab:number, phrases:number,
 *            activeDays:number, byDay:number[]}} byDay 从最早到今天
 */
export function getWeeklyStats(days = 7) {
  const n = Math.max(1, Math.floor(Number(days) || 7));
  const byDay = new Array(n).fill(0);

  // 以「本地今天 00:00」为终点往前推 n 天，避免用 UTC 造成跨日错位
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const start = startOfToday.getTime() - (n - 1) * 86400000;

  let lessons = 0;
  let vocab = 0;
  let phrases = 0;

  const { records } = readProgress();
  Object.values(records).forEach((r) => {
    const t = r?.at ? new Date(r.at).getTime() : NaN;
    if (!Number.isFinite(t) || t < start) return;
    lessons += 1;
    vocab += Number(r.vocab) || 0;
    phrases += Number(r.phrases) || 0;
    const idx = Math.floor((t - start) / 86400000);
    if (idx >= 0 && idx < n) byDay[idx] += 1;
  });

  return {
    days: n,
    lessons,
    vocab,
    phrases,
    activeDays: byDay.filter((x) => x > 0).length,
    byDay,
  };
}

/**
 * 计算整体进度
 * @param {number} totalLessons - 课程总数（由课程接口提供，避免本地硬编码）
 * @returns {{ completed: number, total: number, percent: number }}
 */
export function getProgress(totalLessons) {
  const total = Number(totalLessons) || 0;
  // 只统计仍然存在的课程，避免课程调整后进度虚高
  const completedList = getCompletedLessons();
  const completed = total > 0 ? Math.min(completedList.length, total) : completedList.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { completed, total, percent };
}

/**
 * 找出下一课（第一门未完成的课）
 * @param {Array<Array<Object>>} groups - 按等级分组的课程（每组含 lessons）
 * @returns {Object|null} 下一课，全部完成时返回 null
 */
export function findNextLesson(groups) {
  const completed = new Set(getCompletedLessons());
  for (const group of groups || []) {
    for (const lesson of group.lessons || []) {
      if (!completed.has(lesson.id)) return lesson;
    }
  }
  return null;
}

/**
 * 清空进度
 */
export function resetProgress() {
  try {
    localStorage.removeItem(PROGRESS_KEY);
  } catch (e) {
    console.warn('[courseProgress] 清空进度失败:', e?.message);
  }
}
